import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Search, AssignmentTurnedIn, ContentPaste, Download } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import shipmentService from "../../services/shipmentService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import StatusChip from "../../components/common/StatusChip";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";
import { slugifyTab } from "../../utils/tabSlug";

const TABS = [
  { label: "All", value: "" },
  { label: "D/R Added", value: "added" },
  { label: "D/R Missing", value: "missing" },
  { label: "Assigned to Sailing", value: "assigned" },
  { label: "Sailing Not Assigned", value: "unassigned" },
];

// Same truncate-with-tooltip pattern as AdminShipments.jsx's All Shipments
// table — keeps long descriptions/names from blowing out the row height.
const truncateWords = (text, maxWords = 3) => {
  if (!text) return "—";
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

const URL_SCHEMA = {
  // The tab's slugified label ("all", "d_r_added", …) rather than its array
  // index — stable if tabs are ever reordered, and readable in the address
  // bar/shared links instead of a meaningless number.
  tab: { default: "" },
  search: { default: "" },
  page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
  rowsPerPage: { default: 20, parse: (v) => parseInt(v, 10) || 20 },
};

const AdminDockReceipts = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useScrollRestoration();
  const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
  const { tab, search, page, rowsPerPage } = urlState;
  const activeTab = Math.max(0, TABS.findIndex((t) => slugifyTab(t.label) === tab));
  // Local typing buffer, debounced into urlState.search below — seeded
  // from the URL so a restored search still shows in the field.
  const [searchInput, setSearchInput] = useState(urlState.search);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [assignTracking, setAssignTracking] = useState("");
  const [assignDr, setAssignDr] = useState("");
  const [assigning, setAssigning] = useState(false);

  const [pasteRaw, setPasteRaw] = useState("");
  // Optional, line-matched to pasteRaw — line N here is the tracking
  // number(s) for line N of the sheet paste. When a line has tracking
  // numbers, Process Row(s) assigns them to that row's D/R (plus its
  // weight/CBF) in one call instead of just correcting the D/R's
  // measurements. A line left blank here keeps the old "D/R-only
  // measurement correction" behavior for that row.
  const [pasteTrackingRaw, setPasteTrackingRaw] = useState("");
  const [pasteSubmitting, setPasteSubmitting] = useState(false);

  const [downloadingDr, setDownloadingDr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await shipmentService.listDockReceiptItems({
        filter: TABS[activeTab].value || undefined,
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setItems(data.data.items || []);
      setTotal(data.data.total || 0);
    } catch (err) {
      enqueueSnackbar("Failed to load items", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Debounced search — only fire once typing pauses, not on every keystroke.
  // Skipped when searchInput already matches the URL's committed value —
  // true right after mount (seeded from the same value) — so restoring a
  // search from the URL doesn't also reset page back to 0.
  useEffect(() => {
    if (searchInput === search) return;
    const timer = setTimeout(() => {
      setUrlState({ search: searchInput, page: 0 });
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, search, page, rowsPerPage]);

  // Tracking numbers box accepts one per line OR comma-separated, so a
  // whole batch of items can be consolidated onto a single D/R at once.
  const parseTrackingNumbers = (raw) =>
    raw
      .split(/[\n,]/)
      .map((t) => t.trim())
      .filter(Boolean);

  const trackingCount = parseTrackingNumbers(assignTracking).length;

  const handleAssign = async (e) => {
    e.preventDefault();
    const trackingNumbers = parseTrackingNumbers(assignTracking);
    if (trackingNumbers.length === 0 || !assignDr.trim()) return;

    setAssigning(true);
    try {
      const { data } = await shipmentService.assignDockReceipt(trackingNumbers, assignDr.trim(), {});
      enqueueSnackbar(data.message, { variant: "success" });
      setAssignTracking("");
      setAssignDr("");
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to assign dock receipt", {
        variant: "error",
      });
    } finally {
      setAssigning(false);
    }
  };

  // Paste rows copied straight from the freight forwarder's tracking
  // sheet — tab-separated, in this column order: D/R, Hold, Packages,
  // Remain, Weight, Measure (CBF). Corrects weight/CBF on a D/R that's
  // already linked to item(s) — no tracking number involved. "." is that
  // sheet's placeholder for an empty cell, so it's treated the same as
  // blank. Rows missing a D/R or both weight and CBF are silently dropped
  // from the preview.
  const parsePasteRows = (raw) =>
    raw
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const cols = line.split("\t").map((c) => c.trim());
        const parts = cols.length >= 2 ? cols : line.split(/\s{2,}/).map((c) => c.trim());
        const clean = (v) => (v && v !== "." ? v : "");
        const [dr, , , , weight, cbf] = parts;
        return {
          dockReceiptNumber: clean(dr),
          weight: clean(weight),
          cbf: clean(cbf),
        };
      })
      .filter((r) => r.dockReceiptNumber && (r.weight || r.cbf));

  // pasteTrackingRaw is grouped to pasteRaw by index — group N belongs to
  // row N's D/R. Groups are separated by a BLANK line; within a group,
  // every tracking number for that same D/R can be on its own line
  // (pasting a column of tracking numbers copies one per line) and/or
  // comma/tab separated on one line — all of those tokens are collected
  // together for that row. An empty group (two blank lines in a row, or
  // nothing before the first blank) keeps that row as a pure measurement
  // correction (old behavior, unchanged).
  const pasteTrackingGroups = (() => {
    const groups = [];
    let current = [];
    pasteTrackingRaw.split("\n").forEach((line) => {
      if (line.trim() === "") {
        groups.push(current);
        current = [];
      } else {
        current.push(line);
      }
    });
    groups.push(current);
    return groups;
  })();

  const pasteRows = parsePasteRows(pasteRaw).map((row, idx) => {
    const groupLines = pasteTrackingGroups[idx] || [];
    const trackingNumbers = groupLines
      .flatMap((line) => line.split(/[,\t]+/))
      .map((t) => t.trim())
      .filter(Boolean);
    return { ...row, trackingNumbers };
  });

  const handlePasteSubmit = async () => {
    if (pasteRows.length === 0) return;
    setPasteSubmitting(true);

    // Rows with tracking numbers assign+measure in one call (also marks
    // those items received); rows without one are a pure D/R measurement
    // correction, batched into the existing bulk endpoint same as before.
    const assignRows = pasteRows.filter((r) => r.trackingNumbers.length > 0);
    const measurementOnlyRows = pasteRows.filter((r) => r.trackingNumbers.length === 0);

    let assignedCount = 0;
    const assignFailures = [];
    for (const row of assignRows) {
      try {
        await shipmentService.assignDockReceipt(row.trackingNumbers, row.dockReceiptNumber, {
          weight: row.weight || undefined,
          cbf: row.cbf || undefined,
        });
        assignedCount += 1;
      } catch (err) {
        assignFailures.push(`D/R ${row.dockReceiptNumber}: ${err.response?.data?.message || "failed"}`);
      }
    }

    try {
      if (measurementOnlyRows.length > 0) {
        const { data } = await shipmentService.bulkUpdateDockReceiptMeasurements(measurementOnlyRows);
        const notFoundCount = data.data.notFound?.length || 0;
        const invalidCount = data.data.invalidValue?.length || 0;
        const updatedCount = measurementOnlyRows.length - notFoundCount - invalidCount;
        if (updatedCount > 0) {
          enqueueSnackbar(`Updated weight/CBF on ${updatedCount} dock receipt(s)`, { variant: "success" });
        }
        if (notFoundCount) {
          enqueueSnackbar(`${notFoundCount} dock receipt(s) not found: ${data.data.notFound.join(", ")}`, {
            variant: "warning",
          });
        }
        if (invalidCount) {
          enqueueSnackbar(`${invalidCount} row(s) had an invalid weight/CBF: ${data.data.invalidValue.join(", ")}`, {
            variant: "warning",
          });
        }
      }
      if (assignedCount > 0) {
        enqueueSnackbar(`Assigned ${assignedCount} dock receipt(s) with tracking numbers`, { variant: "success" });
      }
      if (assignFailures.length > 0) {
        enqueueSnackbar(assignFailures.join(" | "), { variant: "error" });
      }
      setPasteRaw("");
      setPasteTrackingRaw("");
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to process rows", {
        variant: "error",
      });
    } finally {
      setPasteSubmitting(false);
    }
  };

  // Bundles every customer-submitted purchase invoice for shipments that
  // have an item under this D/R into one zip — same "download purchase
  // invoices" idea already on the Manifest/Sailing pages, just scoped to a
  // single dock receipt instead of a whole sailing.
  const handleDownloadDrInvoices = async (dockReceiptNumber) => {
    setDownloadingDr(dockReceiptNumber);
    try {
      const response = await shipmentService.getDockReceiptInvoicesZip(dockReceiptNumber);
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `DR-${dockReceiptNumber}-invoices.zip`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      enqueueSnackbar(
        err.response?.status === 404
          ? "No purchase invoices found for this dock receipt"
          : "Failed to download invoices",
        { variant: "error" },
      );
    } finally {
      setDownloadingDr(null);
    }
  };

  // Group the (already D/R-sorted) page of items into D/R buckets so the
  // table can show one header row per dock receipt instead of repeating
  // its number/weight/CBF on every item line. Items with no D/R yet are
  // grouped under a single "No Dock Receipt" bucket.
  const drGroups = [];
  for (const it of items) {
    const key = it.dockReceiptNumber || "";
    const last = drGroups[drGroups.length - 1];
    if (last && last.key === key) {
      last.items.push(it);
    } else {
      drGroups.push({ key, dockReceiptNumber: it.dockReceiptNumber, weight: it.weight, cbf: it.cbf, items: [it] });
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Dock Receipts"
        subtitle="Assign a D/R (dock receipt) number to any item by its tracking number"
      />

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
            Assign Dock Receipt
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Enter one or more tracking numbers (one per line, or comma-separated) to assign them
            all to the same D/R at once — each matching item is also marked as received.
          </Typography>
          <Box
            component="form"
            onSubmit={handleAssign}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <TextField
              size="small"
              label="Tracking Number(s)"
              placeholder={"TBA333002815622\nTBA333006263013"}
              value={assignTracking}
              onChange={(e) => setAssignTracking(e.target.value)}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              helperText={trackingCount > 0 ? `${trackingCount} tracking number(s)` : " "}
            />
            <TextField
              size="small"
              label="Dock Receipt Number (D/R)"
              value={assignDr}
              onChange={(e) => setAssignDr(e.target.value)}
              fullWidth
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<AssignmentTurnedIn />}
              disabled={assigning || trackingCount === 0 || !assignDr.trim()}
              sx={{ height: 40, whiteSpace: "nowrap", gridColumn: { xs: "1", sm: "1 / -1" }, justifySelf: "start" }}
            >
              {assigning ? <CircularProgress size={18} color="inherit" /> : "Assign"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
            Paste from Tracking Sheet
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Paste rows copied directly from the freight forwarder's tracking sheet — tab-separated,
            in this column order: <strong>D/R, Hold, Packages, Remain, Weight, Measure (CBF)</strong>.
            Optionally fill in the tracking number(s) for each row on the right — one per line (or
            comma/tab-separated on one line) if more than one shares that D/R — to assign them to
            that D/R at once, same as Assign Dock Receipt above. Separate different rows' tracking
            numbers with a <strong>blank line</strong>, and leave a group blank to just correct that
            row's weight/CBF instead.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, mb: 1.5 }}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              label="Tracking Sheet"
              placeholder={"1863171\tNo\t1\t1\t14\t0.23"}
              value={pasteRaw}
              onChange={(e) => setPasteRaw(e.target.value)}
              sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
            />
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              label="Tracking Number(s) (optional)"
              placeholder={"TBA333002815622\nTBA333006263013\nTBA333006263014\nTBA333009991111"}
              value={pasteTrackingRaw}
              onChange={(e) => setPasteTrackingRaw(e.target.value)}
              helperText="Group N (separated by a blank line) = tracking number(s) for row N on the left — one per line or comma/tab separated"
              sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
            />
          </Box>

          {pasteRows.length > 0 && (
            <Box sx={{ overflowX: "auto", mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>D/R #</TableCell>
                    <TableCell>Tracking #(s)</TableCell>
                    <TableCell>Weight</TableCell>
                    <TableCell>CBF</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pasteRows.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.dockReceiptNumber}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {r.trackingNumbers.length > 0 ? r.trackingNumbers.join(", ") : "—"}
                      </TableCell>
                      <TableCell>{r.weight || "—"}</TableCell>
                      <TableCell>{r.cbf || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          <Button
            variant="contained"
            startIcon={pasteSubmitting ? <CircularProgress size={18} color="inherit" /> : <ContentPaste />}
            onClick={handlePasteSubmit}
            disabled={pasteSubmitting || pasteRows.length === 0}
          >
            {pasteSubmitting ? "Processing…" : `Process ${pasteRows.length || ""} Row(s)`}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <Box
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setUrlState({ tab: slugifyTab(TABS[v].label), page: 0 })}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              maxWidth: "100%",
              "& .MuiTab-root": {
                fontSize: 13,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              },
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={i} label={t.label} />
            ))}
          </Tabs>
          <Box sx={{ pb: 1, width: { xs: "100%", sm: "auto" } }}>
            <TextField
              size="small"
              placeholder="Search tracking #, D/R #, description…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ width: { xs: "100%", sm: 260 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>

        {loading ? (
          <SkeletonLoader rows={Math.min(8, rowsPerPage)} />
        ) : items.length === 0 ? (
          <EmptyState
            icon="📋"
            title="No items found"
            description="Try adjusting your filters or search."
          />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Tracking #</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Shipment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sailing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drGroups.map((group) => (
                    <React.Fragment key={group.key || "no-dr"}>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell colSpan={6} sx={{ py: 0.75 }}>
                          {group.dockReceiptNumber ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                              <Typography variant="body2" fontWeight={700}>
                                D/R {group.dockReceiptNumber}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Weight: {group.weight != null ? group.weight : "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                CBF: {group.cbf != null ? group.cbf : "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {group.items.length} item(s)
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                  downloadingDr === group.dockReceiptNumber ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <Download fontSize="small" />
                                  )
                                }
                                disabled={downloadingDr === group.dockReceiptNumber}
                                onClick={() => handleDownloadDrInvoices(group.dockReceiptNumber)}
                                sx={{ ml: "auto", fontSize: 11.5 }}
                              >
                                {downloadingDr === group.dockReceiptNumber ? "Downloading…" : "Download Invoices"}
                              </Button>
                            </Box>
                          ) : (
                            <Typography variant="body2" fontWeight={700} color="text.secondary">
                              No Dock Receipt ({group.items.length} item(s))
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      {group.items.map((it) => (
                        <TableRow
                          key={it.itemId}
                          hover
                          onClick={() =>
                            it.shipmentTrackingNumber &&
                            navigate(`/admin/shipments/${it.shipmentTrackingNumber}?item=${it.itemId}`)
                          }
                          sx={{ cursor: it.shipmentTrackingNumber ? "pointer" : "default" }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 12,
                              color: "text.secondary",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {it.trackingNumber || "—"}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Tooltip title={it.description || ""}>
                              <Typography noWrap variant="body2">
                                {truncateWords(it.description, 3)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Tooltip title={`${it.customer?.firstName || ""} ${it.customer?.lastName || ""}`.trim()}>
                              <span>{truncateWords(`${it.customer?.firstName || ""} ${it.customer?.lastName || ""}`.trim(), 2)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}
                          >
                            {it.shipmentTrackingNumber}
                          </TableCell>
                          <TableCell>
                            {it.dockReceiptNumber ? (
                              <StatusChip status={it.itemStatus} domain="item" />
                            ) : (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <StatusChip status={it.itemStatus} domain="item" />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ whiteSpace: "nowrap" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!it.trackingNumber) return;
                                    setAssignTracking((prev) => {
                                      const existing = parseTrackingNumbers(prev);
                                      if (existing.includes(it.trackingNumber)) return prev;
                                      return [...existing, it.trackingNumber].join("\n");
                                    });
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                  }}
                                >
                                  Add D/R
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {it.sailing ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                  {it.sailing.sailingNumber}
                                </Typography>
                                <StatusChip status={it.sailing.status} domain="sailing" />
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Not assigned
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setUrlState({ page: p })}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setUrlState({ rowsPerPage: +e.target.value, page: 0 })}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default AdminDockReceipts;
